import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, IconButton, Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Catálogo de tipos de documentos solicitables del proyecto (ATS).
 * Los tipos creados acá se reutilizan en el modal "Solicitar documentos" del
 * pipeline; y los que se agregan desde ese modal también aparecen acá (mismo
 * catálogo a nivel de proyecto).
 */
const AtsDocumentCatalogTab = () => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();

  const [docs, setDocs] = useState([]); // [{ id?, label, instructions, _key }]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!organization || !project) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await api.get(`/projects/document-catalog`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data || [];
        setDocs(
          (Array.isArray(data) ? data : []).map((d, i) => ({
            id: d.id,
            label: d.label || '',
            instructions: d.instructions || '',
            _key: d.id || `k-${i}`,
          }))
        );
      } catch (err) {
        console.error(err);
        showAlert(t('ats.config.documentCatalog.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRow = () => {
    setDocs((prev) => [
      ...prev,
      { label: '', instructions: '', _key: `new-${Date.now()}-${prev.length}` },
    ]);
  };

  const updateRow = (key, field, value) => {
    setDocs((prev) => prev.map((d) => (d._key === key ? { ...d, [field]: value } : d)));
  };

  const removeRow = (key) => {
    setDocs((prev) => prev.filter((d) => d._key !== key));
  };

  const handleSave = async () => {
    const documents = docs
      .map((d) => ({
        id: d.id,
        label: (d.label || '').trim(),
        instructions: (d.instructions || '').trim(),
      }))
      .filter((d) => d.label);

    setSaving(true);
    try {
      const res = await api.put(
        `/projects/document-catalog`,
        { documents },
        {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.response?.status_response === 'OK') {
        const saved = res.data.response.data || documents;
        setDocs(
          saved.map((d, i) => ({
            id: d.id,
            label: d.label,
            instructions: d.instructions || '',
            _key: d.id || `k-${i}`,
          }))
        );
        showAlert(t('ats.config.documentCatalog.saveSuccess'), 'success');
      } else {
        showAlert(t('ats.config.documentCatalog.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.documentCatalog.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('ats.config.documentCatalog.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ats.config.documentCatalog.description')}
      </Typography>

      {docs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('ats.config.documentCatalog.empty')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {docs.map((d) => (
            <Box key={d._key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                label={t('ats.config.documentCatalog.labelField')}
                value={d.label}
                onChange={(e) => updateRow(d._key, 'label', e.target.value)}
                sx={{ flex: '0 0 38%' }}
              />
              <TextField
                size="small"
                label={t('ats.config.documentCatalog.instructionsField')}
                value={d.instructions}
                onChange={(e) => updateRow(d._key, 'instructions', e.target.value)}
                fullWidth
              />
              <IconButton onClick={() => removeRow(d._key)} sx={{ mt: 0.5 }} aria-label="delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Button startIcon={<AddIcon />} onClick={addRow} sx={{ mb: 3 }}>
        {t('ats.config.documentCatalog.addType')}
      </Button>

      <Divider sx={{ mb: 2 }} />

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('ats.config.documentCatalog.save')}
      </Button>
    </Box>
  );
};

export default AtsDocumentCatalogTab;
